import React, {useContext, useEffect, useState} from "react";
import {Box, Button, Grid, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../context/SignInContext";
import useConfirm from "../../hooks/useConfirm";
import SearchInput from "../../components/inputs/SearchInput";
import {BackendConfigurationRepository} from "../../repositories/BackendConfigurationRepository";
import {BackendConfigurationsTable} from "./components/backend-configurations-table";
import {BackendConfigurationDialog} from "./components/backend-configuration-dialog";
import {SERVICE_FIELDS} from "./serviceFields";

const getErrorMessage = (e, fallback) => e?.response?.data?.message || fallback;

const headerButtonSx = {
  height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 700,
  color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

export const BackendConfigurations = () => {
  const {currentUser, organizations} = useContext(SignInContext);
  const canManage = currentUser?.roles?.some(role => role === "admin" || role === "manager");

  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [search, setSearch] = useState("");

  const confirmDelete = useConfirm(
    "Are you sure you want to delete this backend configuration?",
    "Projects that use this configuration may stop working."
  );

  useEffect(() => {
    BackendConfigurationRepository.getAll()
      .then(response => setConfigurations(response.data || []))
      .catch(e => {
        console.error(e);
        toast.error(getErrorMessage(e, "Failed to load backend configurations"));
      })
      .finally(() => setLoading(false));
  }, []);

  const getOrgName = (orgId) => {
    if (!orgId) return "Public";
    return organizations?.find(o => o.id === orgId)?.name || orgId;
  };

  const query = search.trim().toLowerCase();
  const filteredConfigurations = query
    ? configurations.filter(configuration => [
      configuration.name,
      getOrgName(configuration.organizationId),
      ...SERVICE_FIELDS.map(field => configuration[field.name]),
    ].some(value => String(value || "").toLowerCase().includes(query)))
    : configurations;

  const handleOpenModal = (configuration = null) => {
    setSelectedConfiguration(configuration);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedConfiguration(null);
  };

  const handleSave = async (configuration) => {
    try {
      if (selectedConfiguration?.id) {
        const response = await BackendConfigurationRepository.edit(selectedConfiguration.id, configuration);
        setConfigurations(prev => prev.map(c => c.id === selectedConfiguration.id ? response.data : c));
      } else {
        const response = await BackendConfigurationRepository.create(configuration);
        setConfigurations(prev => [...prev, response.data]);
      }
      toast.success("Backend configuration saved successfully");
      return true;
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to save backend configuration"));
      return false;
    }
  };

  const handleDelete = async (configuration) => {
    const confirmed = await confirmDelete({title: `Delete "${configuration.name}"?`});
    if (!confirmed) return;
    try {
      await BackendConfigurationRepository.delete(configuration.id);
      setConfigurations(prev => prev.filter(c => c.id !== configuration.id));
      toast.success("Backend configuration deleted successfully");
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to delete backend configuration"));
    }
  };

  return (
    <>
      <BackendConfigurationDialog
        open={openModal}
        selectedConfiguration={selectedConfiguration}
        handleClose={handleCloseModal}
        organizations={organizations}
        onSubmit={handleSave}
      />
      <Grid maxWidth={"lg"} sx={{mx: "auto", p: 3, my: 5, borderRadius: 4, bgcolor: "#fefefeee"}} container className={"border-shadow"}>
        <Grid item xs={12}>
          <Stack direction={{xs: "column", md: "row"}} gap={2} alignItems={{xs: "stretch", md: "center"}}>
            <Box sx={{flexShrink: 0}}>
              <Typography variant={"h4"} fontWeight={600}>Backend Configuration</Typography>
              <Typography variant={"body2"} color={"text.secondary"} mt={0.5}>
                Manage the service URLs projects connect to
              </Typography>
            </Box>
            <SearchInput value={search} onChange={setSearch} placeholder={"Search configurations..."}/>
            {canManage && (
              <Button
                sx={{...headerButtonSx, textWrap: "nowrap", flexShrink: 0}}
                startIcon={<HugeiconsIcon icon={Add01Icon} size={18}/>}
                onClick={() => handleOpenModal()}
              >
                Add Configuration
              </Button>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <BackendConfigurationsTable
            configurations={filteredConfigurations}
            loading={loading}
            emptyText={configurations.length === 0 ? "No backend configurations added yet" : `No configurations match "${search}"`}
            getOrgName={getOrgName}
            canManage={canManage}
            onEdit={handleOpenModal}
            onDelete={handleDelete}
          />
        </Grid>
      </Grid>
    </>
  );
};
